import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Statistic, DatePicker, Table, Tag, Progress, message, Spin
} from 'antd';
import {
    BarChart, Bar, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import { AntdSelect } from '../../utils/Antd';
import moment from 'moment';

const { RangePicker } = DatePicker;

export default function QoCDashboard() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialising, setInitialising] = useState(true);
    const [selectedFacility, setSelectedFacility] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [currentPeriod, setCurrentPeriod] = useState(null);
    const [overviewData, setOverviewData] = useState(null);
    const [trendData, setTrendData] = useState([]);
    const [standardScores, setStandardScores] = useState([]);
    const [comparisonData, setComparisonData] = useState([]);

    // Load clients + periods on mount, then set date range from current period
    useEffect(() => {
        loadInitialData();
    }, []);

    // Re-load analytics whenever facility or date range changes
    useEffect(() => {
        if (dateRange?.[0] && dateRange?.[1]) {
            loadDashboardData();
        }
    }, [selectedFacility, dateRange]);

    const loadInitialData = async () => {
        try {
            const [fRes, pRes] = await Promise.all([
                FacilityService.all({ status: 1, include_me: 'Y' }),
                QoCService.getPeriods(),
            ]);

            const allClients = (fRes.data?.result?.data || []).map(v => ({
                id: v.id,
                name: v.business_name,
                site_type: v.site_type || v.level || 'Unknown'
            }));
            setClients(allClients);

            const periods = pRes.data?.data || [];
            const current = periods.find(p => parseInt(p.is_current) === 1) || periods[0] || null;
            setCurrentPeriod(current);

            // Use current period bounds; fall back to current calendar year
            if (current?.period_start && current?.period_end) {
                setDateRange([moment(current.period_start), moment(current.period_end)]);
            } else {
                setDateRange([moment().startOf('year'), moment()]);
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
            setDateRange([moment().startOf('year'), moment()]);
        } finally {
            setInitialising(false);
        }
    };

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const filters = {
                from_date: dateRange?.[0]?.format('YYYY-MM-DD'),
                to_date:   dateRange?.[1]?.format('YYYY-MM-DD')
            };

            const [overview, trends, comparison] = await Promise.all([
                QoCService.getComplianceOverview(filters),
                selectedFacility
                    ? QoCService.getTrendAnalysis(selectedFacility, filters)
                    : Promise.resolve({ data: { data: [] } }),
                QoCService.getComparisonReport(filters)
            ]);

            setOverviewData(overview.data?.data || {});
            setTrendData(trends.data?.data || []);
            setComparisonData(comparison.data?.data || []);

            if (selectedFacility) {
                const standards = await QoCService.getStandardWiseScores(selectedFacility, null);
                setStandardScores(standards.data?.data || []);
            } else {
                setStandardScores([]);
            }
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
            message.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const getColorForScore = (percentage) => {
        if (percentage >= 90) return '#52c41a';
        if (percentage >= 70) return '#faad14';
        if (percentage >= 50) return '#fa8c16';
        return '#f5222d';
    };

    if (initialising) {
        return (
            <div className="page-content">
                <div className="page-pad" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
                    <Spin size="large" tip="Loading dashboard..." />
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>QoC Analytics Dashboard</h2>
                </div>
            </div>

            <div className="page-pad">
                {/* Filters */}
                <Card style={{ marginBottom: '16px' }}>
                    <Row gutter={16} align="bottom">
                        <Col span={10}>
                            <label>Facility</label>
                            <AntdSelect
                                placeholder="All Facilities"
                                showSearch
                                allowClear
                                options={clients}
                                value={selectedFacility}
                                onChange={setSelectedFacility}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col span={10}>
                            <label>Date Range</label>
                            <RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                style={{ width: '100%' }}
                                format="DD MMM YYYY"
                            />
                        </Col>
                        {currentPeriod && (
                            <Col span={4}>
                                <div style={{ fontSize: 11, color: '#888', lineHeight: 1.3 }}>
                                    <div style={{ fontWeight: 600, color: '#1890ff' }}>Current Period</div>
                                    <div>{currentPeriod.label}</div>
                                    <div>{moment(currentPeriod.period_start).format('DD MMM YY')} – {moment(currentPeriod.period_end).format('DD MMM YY')}</div>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Card>

                {/* Overview Statistics */}
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Total Assessments"
                                value={overviewData?.total_assessments || 0}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Average Score"
                                value={parseFloat(overviewData?.average_score || 0).toFixed(1)}
                                suffix="%"
                                valueStyle={{ color: getColorForScore(parseFloat(overviewData?.average_score || 0)) }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="High Performers (≥90%)"
                                value={overviewData?.high_performers || 0}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Need Attention (<70%)"
                                value={overviewData?.need_attention || 0}
                                valueStyle={{ color: '#f5222d' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Trend Analysis */}
                {selectedFacility && trendData.length > 0 && (
                    <Card title="Score Trend Over Time" style={{ marginBottom: '16px' }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="assessment_date"
                                    tickFormatter={(date) => moment(date).format('MMM YY')}
                                />
                                <YAxis domain={[0, 100]} />
                                <Tooltip
                                    formatter={(value) => `${parseFloat(value).toFixed(1)}%`}
                                    labelFormatter={(date) => moment(date).format('DD MMM YYYY')}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="overall_percentage"
                                    stroke="#1890ff"
                                    strokeWidth={2}
                                    name="Overall Score"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                )}

                {/* Standard-Wise Scores */}
                {selectedFacility && standardScores.length > 0 && (
                    <Card title="Performance by Standard" style={{ marginBottom: '16px' }}>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={standardScores}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="standard_name" angle={-45} textAnchor="end" height={100} />
                                <YAxis domain={[0, 100]} />
                                <Tooltip formatter={(value) => `${parseFloat(value).toFixed(1)}%`} />
                                <Legend />
                                <Bar dataKey="percentage" name="Score %" radius={[8, 8, 0, 0]}>
                                    {standardScores.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={getColorForScore(entry.percentage)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                )}

                {/* Facility Comparison */}
                <Card title="Facility Comparison">
                    <Table
                        dataSource={comparisonData}
                        rowKey="facility_id"
                        loading={loading}
                        pagination={{ pageSize: 10 }}
                        columns={[
                            {
                                title: 'Facility',
                                dataIndex: 'facility_name',
                                sorter: (a, b) => a.facility_name.localeCompare(b.facility_name)
                            },
                            {
                                title: 'Level',
                                dataIndex: 'facility_level',
                                width: 160
                            },
                            {
                                title: 'Assessments',
                                dataIndex: 'assessment_count',
                                width: 120,
                                align: 'center'
                            },
                            {
                                title: 'Average Score',
                                dataIndex: 'average_percentage',
                                width: 220,
                                sorter: (a, b) => parseFloat(a.average_percentage || 0) - parseFloat(b.average_percentage || 0),
                                defaultSortOrder: 'descend',
                                render: (percentage) => {
                                    const pct = !isNaN(parseFloat(percentage)) ? parseFloat(percentage) : 0;
                                    return (
                                        <Progress
                                            percent={Math.min(pct, 100)}
                                            strokeColor={getColorForScore(pct)}
                                            format={(p) => `${(p || 0).toFixed(1)}%`}
                                        />
                                    );
                                }
                            },
                            {
                                title: 'Latest',
                                dataIndex: 'latest_assessment_date',
                                width: 120,
                                render: (date) => date ? moment(date).format('DD MMM YY') : 'N/A'
                            }
                        ]}
                    />
                </Card>
            </div>
        </div>
    );
}